import * as React from 'react';
import {
    ArrayInput,
    DateTimeInput,
    SelectInput,
    SimpleFormIterator,
    TextInput,
    required,
    useRecordContext,
} from 'react-admin';
import { formatCampaignWeekdays, parseCampaignWeekdays } from './campaignWeekdays';

const CampaignRecipientInput = () => {
    const record = useRecordContext<{ csv_default_email?: string }>();
    return (
        <TextInput
            source="csv_email"
            type="email"
            helperText="resources.campaign.helpers.csv_email"
            placeholder={record?.csv_default_email || ''}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
        />
    );
};

export const CampaignFormFields = () => (
    <>
        <TextInput source="name" validate={[required()]} fullWidth />
        <SelectInput
            source="status"
            choices={[
                { id: 0, name: 'resources.campaign.status.active' },
                { id: 1, name: 'resources.campaign.status.inactive' },
            ]}
            validate={[required()]}
            defaultValue={0}
            helperText="resources.campaign.helpers.status"
            fullWidth
        />
        <DateTimeInput source="start_date" validate={[required()]} />
        <DateTimeInput source="end_date" validate={[required()]} />
        <ArrayInput source="books" validate={[required()]}>
            <SimpleFormIterator inline>
                <TextInput source="isbn" validate={[required()]} />
                <TextInput source="title" validate={[required()]} />
            </SimpleFormIterator>
        </ArrayInput>
        <SelectInput
            source="target_type"
            choices={[
                {
                    id: 'user_guid',
                    name: 'resources.campaign.target_types.user_guid',
                },
                {
                    id: 'crm_guid',
                    name: 'resources.campaign.target_types.crm_guid',
                },
            ]}
            validate={[required()]}
            defaultValue="user_guid"
            fullWidth
        />
        <TextInput
            source="target_list"
            multiline
            minRows={5}
            validate={[required()]}
            helperText="resources.campaign.helpers.target"
            fullWidth
        />
        <CampaignRecipientInput />
        <TextInput
            source="csv_weekdays"
            format={formatCampaignWeekdays}
            helperText="resources.campaign.helpers.weekdays"
            fullWidth
        />
        <TextInput
            source="csv_time"
            helperText="resources.campaign.helpers.time"
        />
        <TextInput
            source="settings_json"
            multiline
            minRows={4}
            helperText="resources.campaign.helpers.settings"
            fullWidth
        />
    </>
);

interface CampaignFormData {
    [key: string]: unknown;
    settings_json?: string;
    csv_weekdays?: string;
    csv_time?: string;
    csv_email?: string;
    target_type?: string;
    target_list?: string;
}

const toIsoDateTime = (value: unknown, field: string) => {
    const date = value instanceof Date ? value : new Date(String(value || ''));
    if (!Number.isFinite(date.getTime())) {
        throw new Error(`Anna kenttään ${field} kelvollinen päivämäärä ja kellonaika.`);
    }
    return date.toISOString();
};

export const campaignTransform = (data: CampaignFormData) => {
    const additionalSettings = data.settings_json
        ? JSON.parse(data.settings_json)
        : {};
    if (
        !additionalSettings ||
        typeof additionalSettings !== 'object' ||
        Array.isArray(additionalSettings)
    ) {
        throw new Error('Asetusten tulee olla JSON-objekti.');
    }

    const weekdaysValue = String(data.csv_weekdays || '').trim();
    const time = String(data.csv_time || '').trim();
    let csvSchedule: { weekdays: number[]; time: string } | null = null;
    if (weekdaysValue || time) {
        if (!weekdaysValue || !time) {
            throw new Error('Täytä sekä vientipäivät että vientiaika.');
        }
        const weekdays = parseCampaignWeekdays(weekdaysValue);
        if (
            !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time)
        ) {
            throw new Error('Tarkista vientiaika (HH:mm).');
        }
        csvSchedule = { weekdays, time };
    }

    const {
        settings_json,
        csv_weekdays,
        csv_time,
        csv_email,
        target_type,
        target_list,
        targets,
        csv_default_email,
        csv_recipient,
        ...campaign
    } = data;
    if (!['user_guid', 'crm_guid'].includes(String(target_type))) {
        throw new Error('Valitse kohdelistan tunnistetyyppi.');
    }

    return {
        ...campaign,
        start_date: toIsoDateTime(data.start_date, 'start_date'),
        end_date: toIsoDateTime(data.end_date, 'end_date'),
        settings: {
            ...additionalSettings,
            csv_email: String(csv_email || '').trim() || null,
            csv_schedule: csvSchedule,
        },
        targets: String(target_list || '')
            .split(/\r?\n/)
            .map(line => line.trim())
            .filter(Boolean)
            .map(identifier => ({
                type: target_type,
                identifier,
            })),
    };
};
